import { Injectable, inject } from '@angular/core'
import { Observable, map } from 'rxjs'
import { PrivateCameraService } from '../../../_services/camera/camera.service'
import { Camera, CapturingWindowWeek } from '../../../_services/camera/camera.model'

@Injectable({
  providedIn: 'root'
})
export class CameraService {
  private privateCameraService = inject(PrivateCameraService)

  /**
   * Get camera details including capturing windows
   * @param shopId - ID of the shop
   * @param cameraId - ID of the camera
   */
  getCamera(shopId: string, cameraId: string): Observable<Camera | null> {
    return this.privateCameraService.getCamera(shopId, cameraId).pipe(
      map(cameras => {
        if (cameras && cameras.length > 0) {
          return cameras[0]
        }
        return null
      })
    )
  }

  /**
   * Get capturing windows for a specific camera
   * @param shopId - ID of the shop
   * @param cameraId - ID of the camera
   */
  getCapturingWindows(shopId: string, cameraId: string): Observable<CapturingWindowWeek | null> {
    return this.getCamera(shopId, cameraId).pipe(
      map(camera => {
        if (camera && camera.capturing_windows) {
          return camera.capturing_windows
        }
        return null
      })
    )
  }

  /**
   * Check if a specific weekday has recording windows
   * @param capturingWindows - The capturing windows object
   * @param weekday - Weekday number (0 = Sunday, 1 = Monday, ..., 6 = Saturday)
   */
  hasRecordingWindowsForWeekday(
    capturingWindows: CapturingWindowWeek | null,
    weekday: number
  ): boolean {
    if (!capturingWindows) {
      return false
    }

    const weekdayMap: { [key: number]: keyof CapturingWindowWeek } = {
      0: 'sunday',
      1: 'monday',
      2: 'tuesday',
      3: 'wednesday',
      4: 'thursday',
      5: 'friday',
      6: 'saturday'
    }

    const weekdayKey = weekdayMap[weekday]
    if (!weekdayKey) {
      return false
    }

    const windows = capturingWindows[weekdayKey]
    return windows && windows.length > 0
  }

  /**
   * Get a map of weekdays to whether they have recording windows
   * @param capturingWindows - The capturing windows object
   */
  getWeekdayAvailability(capturingWindows: CapturingWindowWeek | null): {
    [weekday: number]: boolean
  } {
    const availability: { [weekday: number]: boolean } = {}

    for (let i = 0; i < 7; i++) {
      availability[i] = this.hasRecordingWindowsForWeekday(capturingWindows, i)
    }

    return availability
  }
}
